import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import initBootstrapComponents from '../../utils/bootstrap';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import axios from "axios";
import { useBasket } from '../../context/BasketContext';
import { Riple } from "react-loading-indicators";
import Modal from 'react-bootstrap/Modal';
import { API_BASE_URL } from '../../constants/api';
import { generateEtaTimeOptions, isValidEtaTime } from '../../utils/etaTimeOptions';

const ScooterVehicleAvailability = () => {

    const { vehicleId, eventId, vehicleType } = useParams();
    const [searchParams] = useSearchParams();
    const slotIdParam = searchParams.get('slot');

    const { addToBasket, basket } = useBasket();
    const navigate = useNavigate();

    const [vehicle, setVehicle] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [loading, setLoading] = useState(false);
    const [eventDetails, setEventDetails] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [bookingErrorMessage, setBookingErrorMessage] = useState("");

    const [etaTime, setEtaTime] = useState("");
    const [over25Stone, setOver25Stone] = useState("");

    const typeLabel = vehicleType === 'wheelchair' ? 'wheelchair' : 'scooter';

    const etaTimeOptions = useMemo(
        () => generateEtaTimeOptions(eventDetails?.start_at, eventDetails?.end_at),
        [eventDetails?.start_at, eventDetails?.end_at]
    );

    useEffect(() => {
        initBootstrapComponents();
        fetchVehicleAndSlots();
        fetchEventDetails();
    }, []);

    const fetchEventDetails = async () => {
        const response = await axios.get(`${API_BASE_URL}/api/events/${eventId}/details?key=7678356c642c91a255471d162205e0f3`);
        setEventDetails(response.data.data);
    };

    const fetchVehicleAndSlots = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/api/events/${eventId}/slots?key=7678356c642c91a255471d162205e0f3&format=vehicles&vehicle_type=${vehicleType}`);
            Object.values(response.data.data).forEach(vehicleData => {
                if (vehicleData.vehicle.id == vehicleId) {
                    setVehicle(vehicleData.vehicle);

                    const slotsArray = [];
                    Object.values(vehicleData.slots).forEach(slotData => {
                        slotsArray.push(slotData);
                    });

                    const matchedSlot = slotsArray.find(slot => String(slot.id) === String(slotIdParam));
                    if (matchedSlot) {
                        setSelectedSlot(matchedSlot);
                    }
                }
            });
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-GB', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const composeEtaAt = () => {
        if (!selectedSlot || !etaTime) return '';
        const date = new Date(selectedSlot.start_at);
        const [hours, minutes] = etaTime.split(':');
        date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
        const pad = (n) => String(n).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    const isFormComplete = selectedSlot
        && etaTime
        && isValidEtaTime(etaTime, eventDetails?.start_at, eventDetails?.end_at)
        && over25Stone !== "";

    const handleBookScooter = async () => {
        if (!isFormComplete) return;

        if (!isValidEtaTime(etaTime, eventDetails?.start_at, eventDetails?.end_at)) {
            setBookingErrorMessage('Please select a valid estimated arrival time during the event.');
            return;
        }

        const existingSlots = basket?.slots || [];
        const slotAlreadyExists = existingSlots.some(existingSlot =>
            existingSlot.id === selectedSlot.id
        );

        if (slotAlreadyExists) {
            setBookingErrorMessage('This date is already reserved for you');
            return;
        }

        try {
            const bookingId = await lockSlots(selectedSlot);
            if (!bookingId) return;

            const newSlot = {
                id: selectedSlot.id,
                booking_id: bookingId,
                vehicle: vehicle,
                start_at: selectedSlot.start_at,
                date: new Date(selectedSlot.start_at),
                eta_at: composeEtaAt(),
                over_25_stone: over25Stone === 'yes' ? 1 : 0
            };

            addToBasket({
                slots: [...existingSlots, newSlot],
                event_id: eventId,
                event_name: eventDetails.name,
            });

            setShowModal(true);
        } catch (error) {
            console.error('Error in booking process:', error);
        }
    };

    const handleConfirmBooking = () => {
        setShowModal(false);
        navigate('/basket-confirmation');
    };

    const handleKeepBrowsing = () => {
        setShowModal(false);
        navigate(`/scooter/event/${eventId}/${vehicleType}/vehicles`);
    };

    const lockSlots = async (slot) => {
        setLoading(true);
        setBookingErrorMessage("");
        try {
            const token = localStorage.getItem('ambassador_token');
            const ambassadorId = localStorage.getItem('ambassador_id');

            const formDataObj = new FormData();
            formDataObj.append('slots[0][slot]', parseInt(slot.id));
            formDataObj.append('slots[0][vehicle]', parseInt(vehicle.id));
            if (ambassadorId) {
                formDataObj.append('ambassador_id', ambassadorId);
            }
            formDataObj.append('key', '7678356c642c91a255471d162205e0f3');
            formDataObj.append('eta_at', composeEtaAt());
            formDataObj.append('over_25_stone', over25Stone === 'yes' ? 1 : 0);
            formDataObj.append('source', token ? 'admin' : 'customer');

            const headers = {
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'multipart/form-data'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const resp = await axios.post(`${API_BASE_URL}/api/events/bookings/book`, formDataObj, {
                headers,
                withCredentials: true
            });

            const errorMessage = resp.data.data.errmessage;
            if (errorMessage) {
                setBookingErrorMessage(errorMessage);
                return null;
            } else {
                const bookingIds = resp.data.data.bookings.map(booking => booking.id);
                setBookingErrorMessage("");
                return bookingIds[0];
            }
        } catch (error) {
            console.error('Error locking slots:', error);
            const apiMessage = error.response?.data?.data?.message;
            if (apiMessage) {
                setBookingErrorMessage(apiMessage);
            } else {
                setBookingErrorMessage("An error occurred while booking the slot.");
            }
            throw error;
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex flex-column justify-content-between min-vh-100">
                <Header />
                <div className="d-flex justify-content-center align-items-center flex-grow-1">
                    <Riple color="#0052BE" size="medium" />
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="d-flex flex-column justify-content-between min-vh-100" id="body-container">
            <Header />

            <main>
                <section className="bg-motability-mid-black-5 py-32 pt-lg-96 pb-lg-87">
                    <div className="container">
                        <h1 className="text-primary mb-4 mb-lg-5 text-capitalize">{typeLabel} details and booking</h1>

                        {!selectedSlot ? (
                            <div className="bg-white rounded-4 p-4 p-lg-32">
                                <p className="mb-0">
                                    We couldn't find the selected date for this {typeLabel}. Please go back and choose a date.
                                </p>
                            </div>
                        ) : (
                            <div className="row row-gap-4 row-gap-lg-0 gx-xxl-5">
                                {/* Details Card */}
                                <div className="col-lg-6">
                                    <div className="bg-white rounded-4 p-4 p-lg-32">
                                        <h2 className="text-primary h3 mb-3">{vehicle?.manufacturer?.name} {vehicle?.model || vehicle?.name}</h2>

                                        {vehicle?.images_array?.[0] && (
                                            <div className="mb-4">
                                                <img
                                                    src={vehicle.images_array[0]}
                                                    alt={vehicle?.name}
                                                    className="img-fluid rounded-3 w-100"
                                                />
                                            </div>
                                        )}

                                        {vehicle?.description && (
                                            <>
                                                <h3 className="text-primary h4 mb-3">Features</h3>
                                                <p className="mb-0">{vehicle.description}</p>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Booking Card */}
                                <div className="col-lg-6">
                                    <div className="bg-white rounded-4 p-4 p-lg-32">
                                        <h2 className="text-primary h3 mb-4 text-capitalize">
                                            Book a {typeLabel}{vehicle?.number ? ` (number ${vehicle.number})` : ''}
                                        </h2>

                                        {/* Selected date summary */}
                                        <div className="mb-4">
                                            <p className="text-primary fw-semibold mb-2">Selected date</p>
                                            <p className="mb-0">{formatDate(selectedSlot.start_at)}</p>
                                        </div>

                                        {/* Estimated arrival time */}
                                        <div className="mb-4">
                                            <label htmlFor="scooter-eta-at" className="text-primary fw-semibold mb-3 d-block">
                                                Estimated arrival time
                                            </label>
                                            <select
                                                id="scooter-eta-at"
                                                className="form-select f-input-lg blue-select-dropdown border-motability-mid-black-40 shadow-motability-mid-black-12-inset w-auto"
                                                value={etaTime}
                                                onChange={(e) => setEtaTime(e.target.value)}
                                            >
                                                <option value="" disabled>-Select-</option>
                                                {etaTimeOptions.map(({ value, label }) => (
                                                    <option key={value} value={value}>{label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Over 25 stone */}
                                        <div className="mb-4">
                                            <p className="text-primary fw-semibold mb-3">
                                                Will the person using the {typeLabel} weigh over 25 stone?
                                            </p>
                                            <div className="d-flex align-items-center gap-5">
                                                <div className="form-check d-flex align-items-center mb-0">
                                                    <input
                                                        name="over_25_stone"
                                                        id="over-25-stone-yes"
                                                        type="radio"
                                                        className="form-check-input cursor-pointer mt-0"
                                                        value="yes"
                                                        checked={over25Stone === 'yes'}
                                                        onChange={(e) => setOver25Stone(e.target.value)}
                                                    />
                                                    <label htmlFor="over-25-stone-yes" className="form-check-label cursor-pointer ps-12">
                                                        Yes
                                                    </label>
                                                </div>
                                                <div className="form-check d-flex align-items-center mb-0">
                                                    <input
                                                        name="over_25_stone"
                                                        id="over-25-stone-no"
                                                        type="radio"
                                                        className="form-check-input cursor-pointer mt-0"
                                                        value="no"
                                                        checked={over25Stone === 'no'}
                                                        onChange={(e) => setOver25Stone(e.target.value)}
                                                    />
                                                    <label htmlFor="over-25-stone-no" className="form-check-label cursor-pointer ps-12">
                                                        No
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        {bookingErrorMessage && (
                                            <div className="alert alert-danger my-3" role="alert">
                                                {bookingErrorMessage}
                                            </div>
                                        )}

                                        <button
                                            type="button"
                                            className="btn btn-primary px-4"
                                            onClick={handleBookScooter}
                                            disabled={!isFormComplete}
                                        >
                                            Add to basket
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="d-flex flex-column align-items-start mt-4 mt-lg-64">
                            <Link to={`/scooter/event/${eventId}/${vehicleType}/vehicles`} className="d-inline-block fw-medium text-decoration-none">
                                ‹ <span className="d-inline-block text-decoration-underline">Back to date selection</span>
                            </Link>
                        </div>
                    </div>
                </section>
            </main>

            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                centered
                aria-labelledby="add-slot-to-basket-modal"
            >
                <Modal.Header className="pt-64 pb-5 border-0 justify-content-center">
                    <h4 className="text-primary text-center mb-0" id="add-slot-to-basket-modal">
                        Add slot to Basket
                    </h4>
                </Modal.Header>

                <Modal.Footer className="gap-1 gap-lg-39 px-3 pt-0 pb-64 justify-content-center border-0">
                    <button
                        type="button"
                        className="btn btn-primary m-0 px-lg-29"
                        onClick={handleConfirmBooking}
                    >
                        Go to Checkout
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-primary fw-normal m-0 px-lg-29"
                        onClick={handleKeepBrowsing}
                    >
                        Continue booking
                    </button>
                </Modal.Footer>
            </Modal>

            <Footer />
        </div>
    );
};

export default ScooterVehicleAvailability;
